<template>
    <div class="event__edit_costs">
        <form-checkbox
            :label="$t('ebs.events.edit.labels.eventHasCost')"
            :name="'event-has-cost'"
            v-model="eventCostsEnabled"
        >{{ $t('ebs.events.edit.title.associateACost') }}
        </form-checkbox>
        <transition-group name="fade" mode="out-in">
            <form-price
                v-if="eventCostsEnabled"
                :label="$t('ebs.events.edit.labels.eventCost')"
                :name="'event-cost'"
                :key="'event-cost'"
                :data-vv-as="$t('forms.dataVvAs.eventCost')"
                :required="true"
                :v-validate="'required'"
                v-model="eventCostsValue"
            />
            <form-checkbox
                :label="$t('ebs.events.edit.labels.costOverrides')"
                :name="'cost-overrides'"
                :key="'cost-overrides'"
                v-if="eventCostsEnabled && user.isTokenFromOldLms()"
                v-model="eventCostsOverridesEnabled"
            >{{ $t('ebs.events.edit.title.changeEventCost') }}
            </form-checkbox>
            <div :key="'cost-overrides-transition'">
                <transition
                    name="fade"
                    mode="out-in"
                >
                    <form-coststable
                        v-if="eventCostsOverridesEnabled"
                        :values="eventCostsSettingsOverrides"
                        :items="services"
                        @input="setEventCostsSettingsOverrides"
                    />
                </transition>
            </div>
        </transition-group>
        <form-submit
            :submit-button-animate-loading="!eventHasOccurrences"
            @reset="eventReset"
            @submit="beforeEventSubmit"
        />
        <pop-notify
            ref="popnotifyvalidation"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'Ok'"
            :submit-button-active="false"
        />
        <pop-notify
            ref="popnotifynotaffectsession"
            :class="'pop__notify--notaffectsession'"
            :title-text="'Save changes?'"
            :content-text="'Your changes will not affect the existing sessions of this event.'"
            :cancel-button-text="$t('ebs.events.edit.notify.buttonClose')"
            :submit-button-text="$t('ebs.events.edit.notify.buttonConfirm')"
            :prevent-close="true"
            @emit-submit="eventSubmit"
        />
    </div>
</template>

<script>
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import FormPrice from '@/components/default/forms/FormPrice'
import FormSubmit from '@/components/default/forms/FormSubmit'
import PopNotify from '@/components/default/shared/PopNotify'
import FormCoststable from '@/components/default/forms/FormCoststable'
import { watchContentChangeEmit } from '@/helpers/mixins'
import { User } from '@/User'

export default {
  data () {
    return {
      user: new User()
    }
  },
  components: {
    FormCoststable,
    PopNotify,
    FormSubmit,
    FormPrice,
    FormCheckbox
  },
  name: 'event-edit-costs',
  mixins: [watchContentChangeEmit],
  computed: {
    services: function () {
      return this.$store.getters['getServices']
    },
    //
    eventCostsEnabled: {
      get () {
        return this.$store.getters['getEventCostsEnabled']
      },
      set (value) {
        this.$store.dispatch('setEventCostsEnabled', value)
      }
    },
    eventCostsValue: {
      get () {
        return this.$store.getters['getEventCostsValue']
      },
      set (value) {
        this.$store.dispatch('setEventCostsValue', value)
      }
    },
    eventCostsOverridesEnabled: {
      get () {
        return this.$store.getters['getEventCostsOverridesEnabled']
      },
      set (value) {
        this.$store.dispatch('setEventCostsOverridesEnabled', value)
      }
    },
    eventCostsSettingsOverrides: {
      get () {
        return this.$store.getters['getEventCostsSettingsOverrides']
      }
    },
    watchContent () {
      return this.eventCostsEnabled + this.eventCostsValue + this.eventCostsOverridesEnabled + this.eventCostsSettingsOverrides
    },
    eventAllowEdit: function () {
      return this.$store.getters.getEventAllowEdit
    },
    eventAllowEditStatus: function () {
      return this.$store.getters.getEventAllowEditStatus
    },
    eventHasOccurrences () {
      return this.$store.getters.getEventHasOccurrences
    },
    eventCustomRequestLoaded () {
      return this.$store.getters.getEventCustomRequestLoaded
    }
  },
  methods: {
    validateBeforeRouteLeave: function (to) {
      if (to.name.includes('ebs-event-edit') || to.name.includes('ebs-event-add')) {
        this.$validator.validateAll().then((result) => {
          if (result) {
            this.$parent.validated = true
            this.$router.push({ path: to.path })
          } else {
            this.$refs.popnotifyvalidation.openNotify()
          }
        })
      } else {
        this.$parent.validated = true
        this.$router.push({ path: to.path })
      }
    },
    allowRedirectBeforeRouteLeave: function () {
      this.$eventBus.$emit('event-submit')
    },
    beforeEventSubmit () {
      if (this.eventHasOccurrences) {
        this.openNotifyNotAffectSessions()
      } else {
        this.eventSubmit()
      }
    },
    openNotifyNotAffectSessions () {
      this.$refs.popnotifynotaffectsession.openNotify()
    },
    closeNotifyNotAffectSessions () {
      this.$refs.popnotifynotaffectsession.closeNotify()
    },
    checkEventStatus (value) {
      if (value === 'active' && this.eventHasOccurrencesDraft) {
        this.$refs.popnotifyhasdraftsessions.openNotify()
      }
    },
    //
    getServices: function () {
      this.$store.dispatch('getServices')
    },
    //
    setEventCostsSettingsOverrides: function (data) {
      this.$store.dispatch('setEventCostsSettingsOverrides', data)
    },
    //
    eventReset: function () {
      this.$store.dispatch('resetEvent')
    },
    eventSubmit: function () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.$route.name.includes('ebs-event-add')) {
            this.$store.dispatch('createEvent')
          } else if (this.$route.name.includes('ebs-event-edit')) {
            this.$store.dispatch('updateEvent')
          }
          this.allowRedirectBeforeRouteLeave()
        } else {
          this.$refs.popnotifyvalidation.openNotify()
        }
      })
    }
  },
  watch: {
    eventCustomRequestLoaded (value) {
      if (value) {
        this.closeNotifyNotAffectSessions()
      }
    }
  },
  mounted () {
    if (this.services === '') {
      this.getServices()
    }
  },
  provide () {
    return {
      $validator: this.$validator
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/default/forms/formprice";
</style>
